import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  AbstractControl,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import {
  Observable,
  startWith,
  switchMap,
  map,
  from,
  firstValueFrom,
  of,
} from 'rxjs';
import { ConsumerEntity } from '../../../consumer/consumer.entity';
import {
  ConsumerEntityService,
  ConsumerEntityState,
} from '../../../consumer/services/consumer.entity.service';
import {
  normalizeOneRelation,
  normalizeManyRelations,
} from '../../../core/decorators/relation.decorator';
import { ExerciseEntity } from '../../../lesson/exercise/exercise.entity';
import { ExerciseEntityService } from '../../../lesson/exercise/services/exercise.entity.service';
import { SubjectEntityService } from '../../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../../subject/subject.entity';
import {
  SequenceBlock,
  SequenceList,
} from '../../interface/sequence-interface';
import { SequenceEntity } from '../../sequence.entity';
import {
  CdkDragDrop,
  CdkDropList,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import {
  MatError,
  MatFormField,
  MatInput,
  MatLabel,
} from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { TranslocoModule } from '@jsverse/transloco';
import { ObjectConsumerMetadataComponent } from '../../../consumer/object-consumer-metadata/object-consumer-metadata.component';
import { MultiComboEntitySearchComponent } from '../../../utils/component/multi-combo-entity-search/multi-combo-entity-search.component';
import {
  getStatusColor,
  getStatusText,
  StateFilterCBComponent,
} from '../../../utils/component/state-filter-combobox/state-cb.component';
import { EntityStateModel } from '../../../core/state/entity.state';
import { Approval } from '../../../core/types';
import { ExplanationEntity } from '../../../explanation/explanation-entity';
import { ExplanationEntityService } from '../../../explanation/services/explanation.entity.service';

@Component({
  selector: 'pixl-exercise-sequence-edit',
  imports: [
    MatDialogTitle,
    FormsModule,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatOption,
    MatSelect,
    MatDividerModule,
    MultiComboEntitySearchComponent,
    CdkDropList,
    DragDropModule,
    MatIcon,
    NgClass,
    StateFilterCBComponent,
    AsyncPipe,
    TranslocoModule,
    MatTabGroup,
    MatTab,
    ObjectConsumerMetadataComponent,
  ],
  templateUrl: './exercise-sequence-edit.component.html',
  styleUrl: './exercise-sequence-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExerciseSequenceEditComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef =
    inject<MatDialogRef<ExerciseSequenceEditComponent>>(MatDialogRef);

  data? = inject<Partial<SequenceEntity>>(MAT_DIALOG_DATA);

  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;
  sequences$!: Observable<SequenceEntity[]>;
  selectedConsumerName$!: Observable<string>;

  exercises$!: Observable<ExerciseEntity[]>;
  private exerciseCache = new Map<string, ExerciseEntity>();
  allSelectedExercises: ExerciseEntity[] = [];
  splitSelectedExercises: ExerciseEntity[][] = [];
  public exerciseSearchResultObservables: Record<
    number,
    Observable<ExerciseEntity[]>
  > = {};

  exercisesSearchQuery: string | null = null;

  explanations$!: Observable<ExplanationEntity[]>;
  private explanationCache = new Map<string, ExplanationEntity>();
  allSelectedExplanations: ExplanationEntity[] = [];
  splitSelectedExplanations: ExplanationEntity[][] = [];
  public explanationSearchResultObservables: Record<
    number,
    Observable<ExplanationEntity[]>
  > = {};

  explanationsSearchQuery: string | null = null;

  currentPage = 1;
  pageSize = 20;

  private store = inject(Store);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private exerciseService = inject(ExerciseEntityService);
  private explanationService = inject(ExplanationEntityService);
  private destroyRef = inject(DestroyRef);

  private cdr = inject(ChangeDetectorRef);

  private thisExercises: SequenceBlock[] = [];
  private thisExplanations: SequenceBlock[] = [];

  public dataSequceneList: SequenceBlock[][] = [];

  public formGroup: FormGroup;

  get sequenceList(): SequenceBlock[][] {
    return (this.formGroup.get('sequence')?.value as SequenceList)?.list || [];
  }

  constructor() {
    const fb = this.fb;
    const data = this.data;

    const initConsumerData: Record<string, unknown> = {};
    const cd = data?.consumerData || {};
    for (const key of Object.keys(cd)) {
      initConsumerData[key] = [cd[key]];
    }

    this.formGroup = this.fb.group({
      name: [data?.name, Validators.required],
      consumerId: data?.consumerId || null,
      subjectId: data?.subjectId || null,
      approval: data?.approval || '',
      sequence: fb.control<SequenceList>(data?.sequence || { list: [] }),
      kind: data?.kind,
      consumerData: fb.group(initConsumerData),
      copyRestricted: data?.copyRestricted || null,
      active: data?.active || false,
      changedBy: data?.changedBy || null,
      version: data?.version || 1,
      sortName: data?.sortName || data?.name?.toLowerCase() || null,
      consumers: [
        normalizeOneRelation<ConsumerEntity>(data?.consumers) ||
        data?.consumerId ||
        null,
      ],
      subjects: [normalizeOneRelation<SubjectEntity>(data?.subjects)],
      exercises: [normalizeManyRelations<ExerciseEntity>(data?.exercises)],
      explanations: [
        normalizeManyRelations<ExplanationEntity>(data?.explanations),
      ],
    });

    // clean out empty sequence blocks in the middle
    const rawList = this.data?.sequence?.list;
    if (Array.isArray(rawList)) {
      const cleaned = rawList.filter(
        (block) => Array.isArray(block) && block.length > 0,
      );
      if (cleaned.length === 0) cleaned.push([]);

      this.formGroup.patchValue({
        sequence: { list: cleaned },
      });
    }
  }

  public ngOnInit() {
    this.loadRelationData();

    this.initSequenceEntities();

    this.selectedConsumerName$ = this.formGroup
      .get('consumers')!
      .valueChanges.pipe(
        startWith(this.formGroup.get('consumers')!.value),
        switchMap((id) =>
          this.store.select(ConsumerEntityState.byId).pipe(
            map((fn) => fn(id)),
            map((c) => c?.name || 'Konsument'),
          ),
        ),
      );
  }

  private getAllIdsFromSequence(): {
    exerciseIds: string[]
    explanationIds: string[]
  } {
    const sequence =
      (this.formGroup.get('sequence')?.value as SequenceList)?.list || [];

    const flat = sequence.flat();

    const exerciseIds: string[] = [];
    const explanationIds: string[] = [];

    flat.forEach((block) => {
      if (block.type === 'exercise') {
        exerciseIds.push(block.id);
      }
      else if (block.type === 'explanation') {
        explanationIds.push(block.id);
      }
    });

    return {
      exerciseIds: [...new Set(exerciseIds)],
      explanationIds: [...new Set(explanationIds)],
    };
  }

  private async initSequenceEntities(): Promise<void> {
    const { exerciseIds, explanationIds } = this.getAllIdsFromSequence();

    const promises = [];

    // Exercises laden
    if (exerciseIds.length > 0) {
      promises.push(
        this.exerciseService.ensureLoaded({
          'filter[id]': exerciseIds.join(','),
        }),
      );
    }

    // Explanations laden
    if (explanationIds.length > 0) {
      promises.push(
        this.explanationService.ensureLoaded({
          'filter[id]': explanationIds.join(','),
        }),
      );
    }

    await Promise.all(promises);

    this.rebuildAllCaches();
  }

  private rebuildAllCaches(): void {
    const exerciseState = this.store.selectSnapshot(
      this.exerciseService.stateClass,
    ) as EntityStateModel<ExerciseEntity>;

    const explanationState = this.store.selectSnapshot(
      this.explanationService.stateClass,
    ) as EntityStateModel<ExplanationEntity>;

    const sequence =
      (this.formGroup.get('sequence')?.value as SequenceList)?.list || [];

    this.exerciseCache.clear();
    this.explanationCache.clear();

    sequence.flat().forEach((block) => {
      if (block.type === 'exercise') {
        const entity = exerciseState.entities[block.id];
        if (entity) {
          this.exerciseCache.set(block.id, entity);
        }
      }

      if (block.type === 'explanation') {
        const entity = explanationState.entities[block.id];
        if (entity) {
          this.explanationCache.set(block.id, entity);
        }
      }
    });
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery),
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery)),
      ),
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(this.subjectService.ensureLoaded(subjectQuery)).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery)),
      ),
    );
  }

  private parseExerciseEntityToBlock(entity: ExerciseEntity): SequenceBlock {
    return {
      id: entity?.id || '',
      type: 'exercise',
      // this kind is one of > 20 different kinds for the exercise itself
      kind: entity.kind || '',
      subKind: '',
    };
  }

  private parseExplanationEntityToBlock(
    entity: ExplanationEntity,
  ): SequenceBlock {
    return {
      id: entity?.id || '',
      // kind and type mixed up in the data. This is either exercise or exercise
      type: 'explanation',
    };
  }

  public checkForErrors(formControl: AbstractControl): string {
    if (formControl.hasError('required')) {
      return 'Eingabe erforderlich';
    }

    return '';
  }

  public receiveExerciseStateChange(
    event: ExerciseEntity[] | null,
    blockIndex: number,
  ): void {
    if (!event) return;

    this.syncSequenceBlock(event, blockIndex, 'exercise', (e) =>
      this.parseExerciseEntityToBlock(e),
    );

    this.allSelectedExercises = event;

    // this.rebuildExerciseCache();
    this.rebuildAllCaches();
  }

  public receiveExplanationStateChange(
    event: ExplanationEntity[] | null,
    blockIndex: number,
  ): void {
    if (!event) return;

    this.syncSequenceBlock(event, blockIndex, 'explanation', (e) =>
      this.parseExplanationEntityToBlock(e),
    );

    this.allSelectedExplanations = event;

    // this.rebuildExplanationCache();
    this.rebuildAllCaches();
  }

  private syncSequenceBlock<T extends { id: string }>(
    selectedItems: T[],
    blockIndex: number,
    type: 'exercise' | 'explanation',
    parser: (item: T) => SequenceBlock,
  ): void {
    const sequenceControl = this.formGroup.get(
      'sequence',
    ) as FormControl<SequenceList>;

    const currentSequence = sequenceControl.value;

    if (!currentSequence?.list) {
      return;
    }

    const updatedList = [...currentSequence.list];

    const currentBlock = [...(updatedList[blockIndex] || [])];

    const selectedIds = new Set(selectedItems.map((item) => item.id));

    const filteredBlock = currentBlock.filter((block) => {
      if (block.type !== type) {
        return true;
      }
      return selectedIds.has(block.id);
    });

    const existingIds = new Set(filteredBlock.map((block) => block.id));

    selectedItems.forEach((item) => {
      if (!existingIds.has(item.id)) {
        filteredBlock.push(parser(item));
      }
    });

    updatedList[blockIndex] = filteredBlock;

    sequenceControl.setValue(
      {
        list: updatedList,
      },
      {
        emitEvent: false,
      },
    );
  }

  public async receiveExerciseSearchQuery(query: string, blockIndex: number) {
    this.exercisesSearchQuery = query?.trim() || null;

    if (this.exercisesSearchQuery && this.exercisesSearchQuery.length >= 3) {
      const searchParams = {
        'filter[idOrNameLike]': this.exercisesSearchQuery,
      };
      await this.exerciseService.ensureLoaded(searchParams);

      this.exerciseSearchResultObservables[blockIndex] = this.store.select(
        this.exerciseService.allByQuery(searchParams),
      );
    }
    else {
      // default paginated mode
      const sequencePageQuery = {
        'page[size]': this.pageSize,
        'page[number]': this.currentPage,
        'sort': 'name',
      };
      await this.exerciseService.ensureLoaded(sequencePageQuery);
      this.exerciseSearchResultObservables[blockIndex] = this.store
        .select(this.exerciseService.allByQuery(sequencePageQuery))
        .pipe(
          map((paginated) => {
            return [...this.allSelectedExercises, ...paginated].filter(
              (seq, index, self) =>
                index === self.findIndex((s) => s.id === seq.id),
            );
          }),
        );
    }
  }

  public async loadMoreExercises(
    event: { pageNumber: number, pageSize: number },
    blockIndex: number,
  ) {
    if (!this.exercisesSearchQuery) {
      const exercisesPageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'name',
      };

      // Load next page from API
      await this.exerciseService.ensureLoaded(exercisesPageQuery);

      // Get current sequences (previously loaded + selected)
      const currentExercises = await firstValueFrom(
        this.exerciseSearchResultObservables[blockIndex] ?? of([]),
      );

      // Combine previously loaded + new ones from store
      this.exerciseSearchResultObservables[blockIndex] = this.store
        .select(this.exerciseService.allByQuery(exercisesPageQuery))
        .pipe(
          map((paginated) => {
            return [
              ...this.allSelectedExercises,
              ...currentExercises,
              ...paginated,
            ].filter(
              (seq, index, self) =>
                index === self.findIndex((s) => s.id === seq.id),
            );
          }),
        );
    }
  }

  public async receiveExplanationSearchQuery(
    query: string,
    blockIndex: number,
  ) {
    this.explanationsSearchQuery = query?.trim() || null;

    if (
      this.explanationsSearchQuery &&
      this.explanationsSearchQuery.length >= 3
    ) {
      const searchParams = {
        'filter[idOrNameLike]': this.explanationsSearchQuery,
      };
      await this.explanationService.ensureLoaded(searchParams);

      this.explanationSearchResultObservables[blockIndex] = this.store.select(
        this.explanationService.allByQuery(searchParams),
      );
    }
    else {
      // default paginated mode
      const sequencePageQuery = {
        'page[size]': this.pageSize,
        'page[number]': this.currentPage,
        'sort': 'name',
      };
      await this.explanationService.ensureLoaded(sequencePageQuery);
      this.explanationSearchResultObservables[blockIndex] = this.store
        .select(this.explanationService.allByQuery(sequencePageQuery))
        .pipe(
          map((paginated) => {
            return [...this.allSelectedExplanations, ...paginated].filter(
              (seq, index, self) =>
                index === self.findIndex((s) => s.id === seq.id),
            );
          }),
        );
    }
  }

  public async loadMoreExplanations(
    event: { pageNumber: number, pageSize: number },
    blockIndex: number,
  ) {
    if (!this.explanationsSearchQuery) {
      const explanationsPageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'name',
      };

      // Load next page from API
      await this.explanationService.ensureLoaded(explanationsPageQuery);

      // Get current sequences (previously loaded + selected)
      const currentExplanations = await firstValueFrom(
        this.explanationSearchResultObservables[blockIndex] ?? of([]),
      );

      // Combine previously loaded + new ones from store
      this.explanationSearchResultObservables[blockIndex] = this.store
        .select(this.explanationService.allByQuery(explanationsPageQuery))
        .pipe(
          map((paginated) => {
            return [
              ...this.allSelectedExplanations,
              ...currentExplanations,
              ...paginated,
            ].filter(
              (seq, index, self) =>
                index === self.findIndex((s) => s.id === seq.id),
            );
          }),
        );
    }
  }

  public reorderExernations(
    event: CdkDragDrop<SequenceBlock[]>,
    blockIndex: number,
  ) {
    const control = this.formGroup.get('sequence');
    if (!control) {
      return;
    }

    const sequenceValue = control.value as SequenceList;
    const newList = [...sequenceValue.list];
    const targetBlock = [...(newList[blockIndex] || [])];
    moveItemInArray(targetBlock, event.previousIndex, event.currentIndex);
    newList[blockIndex] = targetBlock;

    control.setValue({ list: newList });
  }

  public reorderBlocks(event: CdkDragDrop<SequenceBlock[][]>) {
    const control = this.formGroup.get('sequence');
    if (!control) {
      return;
    }

    const sequenceValue = control.value as SequenceList;
    const newList = [...sequenceValue.list];
    moveItemInArray(newList, event.previousIndex, event.currentIndex);

    control.setValue({ list: newList });
  }

  /**
   * TODOs: getter für Status udn farben etc sind nur für exercises verfügbar, müssen dynamisch gemacht werden
   * wenn im dropdown erkläreinheit/exercise ausgewählt wird verschwinden die jeweils anderen. vereinigung beider muss möglich sein.
   */
  public getExerciseName(exerciseId: string): string | undefined {
    return this.exerciseCache.get(exerciseId)?.name;
  }

  public getExplanationName(explanationId: string): string | undefined {
    return this.explanationCache.get(explanationId)?.name;
  }

  public getExerciseApproval(exerciseId: string): Approval {
    return this.exerciseCache.get(exerciseId)?.approval as Approval;
  }

  public getExplanationApproval(explanationId: string): Approval {
    return this.explanationCache.get(explanationId)?.approval as Approval;
  }

  public getBlockName(blockIndex: number): string {
    const sequenceValue = this.formGroup.get('sequence')?.value as
      | SequenceList |
      undefined;
    const currentList = sequenceValue?.list || [];
    if (currentList[blockIndex]?.length <= 1) {
      return 'Aufgabe';
    }
    return 'zusammenhängende Aufgaben';
  }

  public statusChanged(newStatus: string) {
    this.formGroup.controls.approval?.setValue(newStatus);
  }

  public getStateColor(exernation: SequenceBlock): string {
    if (exernation.type == 'exercise') {
      return getStatusColor(this.getExerciseApproval(exernation.id));
    }
    return getStatusColor(this.getExplanationApproval(exernation.id));
  }

  public getStateText(exernation: SequenceBlock): string {
    if (exernation.type == 'exercise') {
      return getStatusText(this.getExerciseApproval(exernation.id));
    }
    return getStatusText(this.getExplanationApproval(exernation.id));
  }

  public addExerciseBlock() {
    // add new values to both the form group and the map which handles the observables for the dropdown field
    this.exerciseSearchResultObservables[
      Object.keys(this.exerciseSearchResultObservables).length
    ] = new Observable<ExerciseEntity[]>();
    this.explanationSearchResultObservables[
      Object.keys(this.explanationSearchResultObservables).length
    ] = new Observable<ExplanationEntity[]>();
    const sequenceControl = this.formGroup.get('sequence');
    const sequenceValue = sequenceControl?.value as SequenceList | undefined;
    const currentList = sequenceValue?.list || [];
    const newList = [...currentList, []];
    sequenceControl?.patchValue({ list: newList });
    this.cdr.markForCheck();
  }

  public getPlaceholder(): string {
    return 'Suchbegriff eingeben um Ergebnisse zu erhalten';
  }

  public onSave(): void {
    if (this.formGroup.invalid) return;

    const formValue = this.formGroup.value;
    const sequenceList = (formValue.sequence?.list as SequenceBlock[][]) || [];

    // clean out empty sequence blocks before saving
    const cleanedList = sequenceList.filter(
      (block) => Array.isArray(block) && block.length > 0,
    );

    const cleanedSequence: SequenceList = {
      list: cleanedList.length > 0 ? cleanedList : [[]],
    };

    const updatedSequence = Object.assign(
      new SequenceEntity(),
      { id: this.data?.id },
      formValue,
      {
        sequence: cleanedSequence,
        consumers: formValue.consumers ? { id: formValue.consumers } : [],
        subjects: formValue.subjects ? { id: formValue.subjects } : [],
        exercises: (formValue.exercises as string[])?.length ?
          (formValue.exercises as string[]).map((id) => ({ id })) :
          [],
        explanations: (formValue.explanations as string[])?.length ?
          (formValue.explanations as string[]).map((id) => ({ id })) :
          [],
      },
    );

    this.dialogRef.close(updatedSequence);
  }

  public onCancel(): void {
    this.dialogRef.close();
  }
}
